import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';

const CATEGORIES = [
  'Bug Report',
  'Feature Request',
  'Account Issue',
  'Other',
];

const ContactUs: React.FC = () => {
  const { user } = useAuth();
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!category) {
      setError('Please select a category');
      return;
    }

    if (message.trim().length < 10) {
      setError('Please provide more details (at least 10 characters)');
      return;
    }

    setLoading(true);

    try {
      await api.post('/support/contact', {
        category,
        message: message.trim(),
      });

      setSuccess(true);
      setCategory('');
      setMessage('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Contact Us
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Have a question, found a bug, or want to request a feature? We'd love to hear from you!
      </Typography>

      <Paper sx={{ p: 4 }}>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Message sent!
            </Typography>
            <Typography variant="body2">
              Thank you for reaching out. We've sent a confirmation to {user?.email} and will get back to you within 24-48 hours.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={() => setSuccess(false)}
            >
              Send Another Message
            </Button>
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sending as: <strong>{user?.email}</strong>
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                id="category"
                value={category}
                label="Category"
                onChange={handleCategoryChange}
                required
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={6}
              label="Your Message"
              placeholder="Please describe your issue, question, or suggestion in detail..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              inputProps={{ minLength: 10, maxLength: 5000 }}
              helperText={`${message.length}/5000 characters`}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ContactUs;
